import { useEffect, useSyncExternalStore } from "react";

/**
 * The visitor's own recent searches, for Home's "Pick up where you left off".
 *
 * Kept in this browser only (localStorage), never sent anywhere: a search is
 * recorded when a results page opens (the shell watches the address, so no
 * search form has to remember to call this), and Home reads it back. What is
 * kept is what the results address already says (place, dates, travellers)
 * and the address itself, so a card reopens exactly that search.
 *
 * A search whose first day has passed is not offered again: it can no longer
 * be booked. Storage can be missing or blocked (a private window, a test):
 * every read and write is guarded and the list is then simply empty.
 */

const KEY = "toukky_recent_searches";
const VERSION = 1;
const KEEP = 6;
const CHANGED = "toukky:recent-searches";
const DATE = /^\d{4}-\d{2}-\d{2}$/;
const EMPTY = Object.freeze([]);

function store() {
  try { return typeof window === "undefined" ? null : window.localStorage; } catch { return null; }
}

/** Today in the visitor's own calendar, as YYYY-MM-DD. */
export function localToday(now = new Date()) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

const text = (value, max = 120) => {
  const clean = (value || "").trim();
  return clean && clean.length <= max ? clean : "";
};
const date = (value) => (DATE.test(value || "") ? value : "");
const count = (value) => {
  const n = Number.parseInt(value, 10);
  return Number.isInteger(n) && n > 0 && n < 100 ? n : null;
};

/**
 * The search a results address describes, or null when it is not one
 * (a landing page, a detail page, a half-filled form).
 */
export function searchFromLocation(pathname, search) {
  const q = new URLSearchParams(search || "");
  const href = `${pathname}${search || ""}`;
  if (pathname === "/hotels") {
    const place = text(q.get("city"));
    const start = date(q.get("check_in"));
    const end = date(q.get("check_out"));
    if (!place || !start || !end || end <= start) return null;
    return { kind: "stays", href, place, start, end, people: count(q.get("guests")) };
  }
  if (pathname === "/flights") {
    const from = text(q.get("origin_city"), 60);
    const to = text(q.get("destination_city"), 60);
    const start = date(q.get("date"));
    if (!from || !to || !start) return null;
    const end = date(q.get("return_date"));
    return { kind: "flights", href, place: `${from}|${to}`, from, to, start, end: end > start ? end : "", people: count(q.get("passengers")) };
  }
  if (pathname === "/cars") {
    const place = text(q.get("city"), 160);
    const start = date(q.get("check_in"));
    const end = date(q.get("check_out"));
    if (!place || !start) return null;
    return { kind: "cars", href, place, start, end: end >= start ? end : "", people: null };
  }
  return null;
}

const valid = (item) => item && typeof item === "object"
  && ["stays", "flights", "cars"].includes(item.kind)
  && typeof item.href === "string" && item.href.startsWith(`/${item.kind === "stays" ? "hotels" : item.kind}?`)
  && typeof item.place === "string" && item.place
  && DATE.test(item.start || "");

function parse(raw, today) {
  try {
    const data = JSON.parse(raw || "null");
    if (!data || data.v !== VERSION || !Array.isArray(data.items)) return EMPTY;
    const items = data.items.filter((item) => valid(item) && item.start >= today);
    return items.length ? items : EMPTY;
  } catch {
    return EMPTY;
  }
}

function readRaw() {
  try { return store()?.getItem(KEY) ?? null; } catch { return null; }
}

function write(items) {
  try {
    const s = store();
    if (!s) return;
    if (items.length) s.setItem(KEY, JSON.stringify({ v: VERSION, items }));
    else s.removeItem(KEY);
  } catch { /* storage full or blocked: nothing is remembered */ }
  try { window.dispatchEvent(new Event(CHANGED)); } catch { /* no window */ }
}

/** The searches still worth offering, newest first. */
export function readRecentSearches(today = localToday()) {
  return parse(readRaw(), today);
}

/**
 * The flight still to book after a stay booked from a "Hotel + Flight" search
 * (pages/flighthotel): that search opens the stays results with `combo` and
 * `origin` in the address, so the recorded stays search over the same nights
 * says where the traveller flies from. The combination keys name their parts
 * (h a stay, f a flight, c a car): only one with an f has a flight to offer.
 * Null when no such search is remembered on this browser.
 */
export function plannedFlight(checkIn, checkOut, today = localToday()) {
  if (!DATE.test(checkIn || "") || !DATE.test(checkOut || "")) return null;
  for (const item of readRecentSearches(today)) {
    if (item.kind !== "stays" || item.start !== checkIn || item.end !== checkOut) continue;
    const q = new URLSearchParams(item.href.slice(item.href.indexOf("?") + 1));
    if (!/f/.test(q.get("combo") || "")) continue;
    return { from: text(q.get("origin"), 60), to: item.place, people: item.people };
  }
  return null;
}

/** Remembers the search a results address describes; anything else is ignored. */
export function recordSearch(pathname, search, today = localToday()) {
  const entry = searchFromLocation(pathname, search);
  if (!entry || entry.start < today) return false;
  const same = (item) => item.kind === entry.kind && item.place.toLowerCase() === entry.place.toLowerCase()
    && item.start === entry.start && item.end === entry.end;
  const kept = readRecentSearches(today).filter((item) => !same(item));
  write([entry, ...kept].slice(0, KEEP));
  return true;
}

/** Forgets every recent search; returns them, so the visitor can undo it. */
export function clearRecentSearches() {
  const previous = readRecentSearches();
  write([]);
  return previous;
}

/** Puts back the searches a clear removed (the "Undo" after "Clear all"). */
export function restoreRecentSearches(items = []) {
  const kept = Array.isArray(items) ? items.slice(0, KEEP) : [];
  if (kept.length) write(kept);
}

// useSyncExternalStore needs the same array back while nothing changed.
let snapshot = { raw: undefined, today: undefined, items: EMPTY };
function getSnapshot() {
  const raw = readRaw();
  const today = localToday();
  if (raw !== snapshot.raw || today !== snapshot.today) snapshot = { raw, today, items: parse(raw, today) };
  return snapshot.items;
}
const getServerSnapshot = () => EMPTY;

function subscribe(onChange) {
  window.addEventListener(CHANGED, onChange);
  // Another tab searched or cleared.
  window.addEventListener("storage", onChange);
  return () => {
    window.removeEventListener(CHANGED, onChange);
    window.removeEventListener("storage", onChange);
  };
}

/** The recent searches, kept current when one is added or the list is cleared. */
export function useRecentSearches() {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/** Records each results page the visitor opens (called once, by the site header). */
export function useRecordSearches(pathname, search) {
  useEffect(() => { recordSearch(pathname, search); }, [pathname, search]);
}
